import {z} from 'zod';

import {IsoUtcDateTimeSchema, UuidV4Schema} from './common.js';
import {
  DeploymentEnvironmentSchema,
  InstagramAccountSchema,
} from './platform.js';

/**
 * Contratos da publicação pela API oficial da Meta.
 *
 * Quatro regras atravessam o arquivo inteiro, e nenhuma delas é preferência de
 * estilo:
 *
 * 1. **Versão e host são explícitos.** Não existe `latest`, não existe host
 *    implícito. Deixar a Meta escolher a versão é deixá-la trocar o formato de
 *    uma resposta no meio de uma publicação que o dono já aprovou.
 * 2. **Leitura indisponível nunca vira zero.** `publishingLimit` é união
 *    discriminada: quando a leitura falhou, os números não existem no tipo, e
 *    não é possível gravar `0` por engano. Cota zerada e cota desconhecida
 *    levariam a operação a decisões opostas.
 * 3. **Segredo não tem campo.** Não existe lugar para o valor do token, nem
 *    para um prefixo dele. O que o painel vê é validade e situação.
 * 4. **Estado remoto é enum fechado.** Um `status_code` que não está na lista
 *    não é "provavelmente em progresso": é divergência de contrato, e quem
 *    consome precisa parar em vez de adivinhar.
 */

// ---------------------------------------------------------------------------
// Host e versão da Graph
// ---------------------------------------------------------------------------

/**
 * Hosts que este sistema aceita chamar.
 *
 * Lista de PERMISSÃO. Host novo entra aqui de propósito, com revisão. Uma lista
 * de bloqueio deixaria passar calado o dia em que um redirecionamento, ou um
 * campo de resposta, apontasse para outro domínio.
 */
export const ALLOWED_GRAPH_HOSTS = ['graph.instagram.com'] as const;
export const GraphHostSchema = z.enum(ALLOWED_GRAPH_HOSTS);
export type GraphHost = z.infer<typeof GraphHostSchema>;

export const GRAPH_VERSION_PATTERN = /^v\d+\.\d+$/u;

/**
 * `v23.0`, e nunca `latest` nem vazio.
 *
 * Sem valor padrão de propósito: `META_GRAPH_VERSION` ausente precisa derrubar
 * o processo na largada, e não aparecer como uma publicação estranha três
 * semanas depois.
 */
export const GraphVersionSchema = z
  .string()
  .regex(
    GRAPH_VERSION_PATTERN,
    'a versão da Graph precisa ser no formato vNN.N',
  );

export const MetaConfigSchema = z.strictObject({
  host: GraphHostSchema,
  graphVersion: GraphVersionSchema,
  /**
   * Chamada sem tempo limite é chamada que pode ficar pendurada para sempre, e
   * uma publicação pendurada é exatamente o resultado ambíguo que este plano
   * inteiro existe para evitar. Por isso é obrigatório e positivo.
   */
  requestTimeoutMs: z.int().positive().max(120_000),
});
export type MetaConfig = z.infer<typeof MetaConfigSchema>;

// ---------------------------------------------------------------------------
// Escopos
// ---------------------------------------------------------------------------

/** Os quatro escopos aprovados para o aplicativo, na ordem da especificação. */
export const META_SCOPES = [
  'instagram_business_basic',
  'instagram_business_content_publish',
  'instagram_business_manage_messages',
  'instagram_business_manage_insights',
] as const;
export const MetaScopeSchema = z.enum(META_SCOPES);
export type MetaScope = z.infer<typeof MetaScopeSchema>;

// ---------------------------------------------------------------------------
// Situação da conta
// ---------------------------------------------------------------------------

/**
 * O mesmo enum do plano `01`, DERIVADO do schema da conta em vez de recopiado.
 *
 * Duas listas iguais escritas à mão divergem no dia em que uma ganha um estado
 * novo, e a divergência aparece como uma conta que o painel considera
 * inexistente enquanto o banco a considera saudável.
 */
export const AccountOperationalStatusSchema =
  InstagramAccountSchema.shape.status;
export type AccountOperationalStatus = z.infer<
  typeof AccountOperationalStatusSchema
>;
export const ACCOUNT_OPERATIONAL_STATUSES =
  AccountOperationalStatusSchema.options;

/**
 * Estados de contêiner que a Meta documenta.
 *
 * Valor fora desta lista não é tratado como "em progresso": vira divergência de
 * contrato. Um estado novo que a esteira interpretasse como progresso deixaria
 * o trabalho girando até o slot vencer, sem ninguém entender por quê.
 */
export const CONTAINER_STATUSES = [
  'IN_PROGRESS',
  'FINISHED',
  'PUBLISHED',
  'ERROR',
  'EXPIRED',
] as const;
export const ContainerStatusSchema = z.enum(CONTAINER_STATUSES);
export type ContainerStatus = z.infer<typeof ContainerStatusSchema>;

// ---------------------------------------------------------------------------
// Estado do trabalho de publicação
// ---------------------------------------------------------------------------

export const PUBLISHING_JOB_STATES = [
  'QUEUED',
  'PREPARING',
  'CONTAINER_CREATING',
  'CONTAINER_PROCESSING',
  'READY_TO_PUBLISH',
  'PUBLISHING',
  'PUBLISHED',
  'UNCERTAIN',
  'RECONCILING',
  'PAUSED',
  'FAILED',
  'EXPIRED',
  'CANCELLED',
] as const;
export const PublishingJobStateSchema = z.enum(PUBLISHING_JOB_STATES);
export type PublishingJobState = z.infer<typeof PublishingJobStateSchema>;

/**
 * De onde não se sai.
 *
 * `PUBLISHED` é irreversível do ponto de vista da esteira: despublicar é ação
 * externa e auditada, e não uma transição de volta que apagaria o rastro.
 */
export const TERMINAL_PUBLISHING_JOB_STATES = [
  'PUBLISHED',
  'EXPIRED',
  'CANCELLED',
] as const satisfies readonly PublishingJobState[];

// ---------------------------------------------------------------------------
// Saúde do token
// ---------------------------------------------------------------------------

export const MetaTokenStatusSchema = z.enum([
  'VALID',
  'EXPIRING',
  'EXPIRED',
  'REVOKED',
  'UNKNOWN',
]);
export type MetaTokenStatus = z.infer<typeof MetaTokenStatusSchema>;

/**
 * O que o painel sabe sobre o token.
 *
 * Não existe campo para o valor, nem para os primeiros caracteres dele. Um
 * prefixo "só para conferir qual token é" é o começo de um vazamento por
 * captura de tela.
 */
export const MetaTokenHealthSchema = z.strictObject({
  status: MetaTokenStatusSchema,
  issuedAt: IsoUtcDateTimeSchema,
  expiresAt: IsoUtcDateTimeSchema,
  lastCheckedAt: IsoUtcDateTimeSchema,
});
export type MetaTokenHealthDto = z.infer<typeof MetaTokenHealthSchema>;

// ---------------------------------------------------------------------------
// Limite de publicação: indisponível não é zero
// ---------------------------------------------------------------------------

/**
 * O limite remoto de publicações.
 *
 * União discriminada, e não campos anuláveis soltos, porque a diferença entre
 * "a conta usou 0 de 100" e "não consegui perguntar quanto a conta usou" é a
 * diferença entre publicar e não publicar. Com campos soltos, um `0` de
 * inicialização passaria pelo schema e liberaria a publicação.
 *
 * `localReservations` existe nos dois lados: as reservas são NOSSAS, e nós
 * sabemos contá-las mesmo quando a Meta não responde.
 */
export const PublishingLimitSchema = z
  .discriminatedUnion('availability', [
    z.strictObject({
      availability: z.literal('AVAILABLE'),
      quotaUsage: z.int().nonnegative(),
      quotaTotal: z.int().positive(),
      localReservations: z.int().nonnegative(),
      checkedAt: IsoUtcDateTimeSchema,
    }),
    z.strictObject({
      availability: z.literal('UNAVAILABLE'),
      quotaUsage: z.null(),
      quotaTotal: z.null(),
      localReservations: z.int().nonnegative(),
      checkedAt: IsoUtcDateTimeSchema.nullable(),
    }),
  ])
  .refine(
    (limite) =>
      limite.availability === 'UNAVAILABLE' ||
      limite.quotaUsage <= limite.quotaTotal,
    {
      message: 'uso maior que o total não é leitura válida de limite',
      path: ['quotaUsage'],
    },
  );
export type PublishingLimitDto = z.infer<typeof PublishingLimitSchema>;

// ---------------------------------------------------------------------------
// Capacidades da conta
// ---------------------------------------------------------------------------

/** Identificador numérico da Meta. Recusado por formato, nunca "limpo". */
export const MetaNumericIdSchema = z.string().regex(/^\d{1,32}$/u);

export const MetaCapabilitiesSchema = z.strictObject({
  /** Conta LÓGICA (linha de `instagram_accounts`), não o id do Instagram. */
  accountId: UuidV4Schema,
  environment: DeploymentEnvironmentSchema,
  igUserId: MetaNumericIdSchema.nullable(),
  username: z.string().min(1).max(60).nullable(),
  accountType: z.literal('BUSINESS').nullable(),
  graphVersion: GraphVersionSchema,
  status: AccountOperationalStatusSchema,
  grantedScopes: z.array(MetaScopeSchema).max(META_SCOPES.length),
  missingScopes: z.array(MetaScopeSchema).max(META_SCOPES.length),
  /** Nulo antes da conexão. Nulo é "não existe", e não "expirado". */
  token: MetaTokenHealthSchema.nullable(),
  publishingLimit: PublishingLimitSchema,
});
export type MetaCapabilitiesDto = z.infer<typeof MetaCapabilitiesSchema>;

export const CreateInstagramAccountRequestSchema = z.strictObject({
  environment: DeploymentEnvironmentSchema,
  graphVersion: GraphVersionSchema,
});
export type CreateInstagramAccountRequest = z.infer<
  typeof CreateInstagramAccountRequestSchema
>;

// ---------------------------------------------------------------------------
// Trabalho de publicação
// ---------------------------------------------------------------------------

export const ManifestHashSchema = z.string().regex(/^[0-9a-f]{64}$/u);

export const PublishingJobSchema = z.strictObject({
  id: UuidV4Schema,
  accountId: UuidV4Schema,
  slotId: UuidV4Schema,
  contentVersionId: UuidV4Schema,
  /** O hash que o dono aprovou. Publicar outro é publicar peça não revisada. */
  manifestHash: ManifestHashSchema,
  /** A versão usada NESTE trabalho, e não a configurada agora. */
  graphVersion: GraphVersionSchema,
  state: PublishingJobStateSchema,
  attemptCount: z.int().nonnegative(),
  containerId: MetaNumericIdSchema.nullable(),
  remoteMediaId: MetaNumericIdSchema.nullable(),
  remoteStatus: ContainerStatusSchema.nullable(),
  scheduledAt: IsoUtcDateTimeSchema,
  /** Depois disto o trabalho vence. Slot atrasado não vira rajada. */
  expiresAt: IsoUtcDateTimeSchema,
  uncertainSince: IsoUtcDateTimeSchema.nullable(),
  nextAttemptAt: IsoUtcDateTimeSchema.nullable(),
  /**
   * Só a CLASSE do erro, nunca a mensagem da Meta nem o corpo bruto. Mensagem
   * remota é texto de fora e já chegou carregando conteúdo alheio antes.
   */
  lastErrorClass: z.string().min(1).max(60).nullable(),
  createdAt: IsoUtcDateTimeSchema,
  updatedAt: IsoUtcDateTimeSchema,
});
export type PublishingJobDto = z.infer<typeof PublishingJobSchema>;

export const PublishingJobEventSchema = z.strictObject({
  sequence: z.int().positive(),
  /** Nulo só no primeiro evento, quando o trabalho nasce. */
  fromState: PublishingJobStateSchema.nullable(),
  toState: PublishingJobStateSchema,
  code: z.string().min(1).max(80),
  occurredAt: IsoUtcDateTimeSchema,
});
export type PublishingJobEventDto = z.infer<typeof PublishingJobEventSchema>;

export const QuotaReservationStateSchema = z.enum([
  'none',
  'active',
  'released',
  'expired',
]);
export type QuotaReservationState = z.infer<typeof QuotaReservationStateSchema>;

export const PublishingJobDetailSchema = PublishingJobSchema.extend({
  events: z.array(PublishingJobEventSchema),
  approvalId: UuidV4Schema,
  quotaReservationState: QuotaReservationStateSchema,
});
export type PublishingJobDetailDto = z.infer<typeof PublishingJobDetailSchema>;

/**
 * A decisão humana sobre um resultado incerto.
 *
 * `remoteMediaId` só existe em `ACKNOWLEDGE_PUBLISHED` porque só ali ele quer
 * dizer alguma coisa. O motivo é longo de propósito: esta é a decisão que
 * fecha uma ambiguidade sem prova remota, e daqui a seis meses alguém vai
 * precisar entender por quê.
 */
const ResolveReasonSchema = z.string().trim().min(10).max(1_000);

export const ResolveUncertainRequestSchema = z.discriminatedUnion('decision', [
  z.strictObject({
    decision: z.literal('ACKNOWLEDGE_PUBLISHED'),
    remoteMediaId: MetaNumericIdSchema.nullable(),
    reason: ResolveReasonSchema,
  }),
  z.strictObject({
    decision: z.literal('CANCEL'),
    reason: ResolveReasonSchema,
  }),
  z.strictObject({
    decision: z.literal('REQUEST_NEW_VERSION'),
    reason: ResolveReasonSchema,
  }),
]);
export type ResolveUncertainRequest = z.infer<
  typeof ResolveUncertainRequestSchema
>;

// ---------------------------------------------------------------------------
// Alertas
// ---------------------------------------------------------------------------

export const AlertSeveritySchema = z.enum(['info', 'warning', 'critical']);
export type AlertSeverity = z.infer<typeof AlertSeveritySchema>;

export const AlertSchema = z.strictObject({
  id: UuidV4Schema,
  /** Nulo quando o alerta é do sistema, e não de uma conta. */
  accountId: UuidV4Schema.nullable(),
  code: z.string().min(1).max(80),
  severity: AlertSeveritySchema,
  state: z.enum(['open', 'acknowledged', 'resolved']),
  resourceType: z.enum([
    'account',
    'publishing_job',
    'webhook',
    'backup',
    'system',
  ]),
  /**
   * Referência opaca de operação, do mesmo tipo que a auditoria usa. Não é
   * caminho de volta para conversa nem para pessoa.
   */
  operationRef: UuidV4Schema.nullable(),
  openedAt: IsoUtcDateTimeSchema,
  acknowledgedAt: IsoUtcDateTimeSchema.nullable(),
  resolvedAt: IsoUtcDateTimeSchema.nullable(),
});
export type AlertDto = z.infer<typeof AlertSchema>;

// ---------------------------------------------------------------------------
// Modo de operação
// ---------------------------------------------------------------------------

/**
 * O que a operação pode fazer agora.
 *
 * O `refine` existe porque um DTO que diz "pausado" e ao mesmo tempo
 * "publicação permitida" é exatamente o defeito que o botão de emergência não
 * pode ter. Aqui ele não chega a existir: o schema recusa.
 *
 * `publishingAllowed` e `messagingAllowed` são CALCULADOS pelo resolvedor de
 * permissão operacional, e não colunas soltas. `effective` sozinho não decide:
 * conta fora de `active`, leitura de limite indisponível ou bloqueador crítico
 * também fecham as duas portas.
 */
export const OperationModeSchema = z
  .strictObject({
    accountId: UuidV4Schema,
    effective: z.enum(['running', 'paused']),
    publishingAllowed: z.boolean(),
    messagingAllowed: z.boolean(),
    blockers: z.array(z.string().min(1).max(80)).max(20),
    revision: z.int().nonnegative(),
    changedAt: IsoUtcDateTimeSchema,
    /** Nulo quando quem mudou foi o próprio sistema, por bloqueador. */
    changedBy: UuidV4Schema.nullable(),
  })
  .refine(
    (modo) =>
      modo.effective !== 'paused' ||
      (!modo.publishingAllowed && !modo.messagingAllowed),
    {
      message: 'operação pausada não permite publicar nem responder',
      path: ['effective'],
    },
  );
export type OperationModeDto = z.infer<typeof OperationModeSchema>;
